import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import Uppy, { type UppyFile, type Meta, type Body } from '@uppy/core'
import { nanoid } from 'nanoid'
import { usesStorageEmulator } from '@/firebase/storage-routing'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  kGcsUserFilesPipelineBucketRef,
  kUppyUploadAllowedFileTypes,
  kUppyUploadMaxConcurrentEmulatorUploads,
  kUppyUploadMaxConcurrentUploads,
  kUppyUploadMaxFileSizeBytes,
} from '../constants'
import { initUploadBatch } from '../fetch/init-upload'
import { UppyFileUploadActions, type FileUploadState } from '../store/slice'
import { selectIsUploading, selectQueuedFiles } from '../store/selectors'
import { runUppyUploadPipeline } from '../utils/run-uppy-upload-pipeline'
import { uploadInitializedUserFileBlob, type ActiveUploadTask } from '../utils/upload-initialized-user-file-blob'

type AnyUppyFile = UppyFile<Meta, Body>

export type FileUploadContextValue = {
  uppy: Uppy
  uploadTasksRef: React.MutableRefObject<Record<string, ActiveUploadTask>>
  isStartingUploadRef: React.MutableRefObject<boolean>
}

const FileUploadContext = createContext<FileUploadContextValue | null>(null)

export function useFileUploadContext(): FileUploadContextValue {
  const ctx = useContext(FileUploadContext)
  if (!ctx) throw new Error('useFileUploadContext must be used within FileUploadProvider')
  return ctx
}

/**
 * Owns the shared Uppy instance and fallback upload driver for all matter-scoped uploads.
 *
 * Mounting this above the router (at MatterLayout) ensures the Uppy instance — and
 * the in-memory file blobs it holds — survive route changes within a matter. The
 * fallback auto-start effect picks up any queued files that were stranded when their
 * host component unmounted mid-queue (e.g. the user navigated away from Drive during
 * a large upload).
 *
 * The isStartingUploadRef guard prevents this fallback from racing with the
 * component-level driver in useDriveUpload: child effects run before parent effects,
 * so the component's startUpload sets the ref to true before this effect checks it.
 */
export function FileUploadProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch()

  const [uppy] = useState(
    () =>
      new Uppy({
        restrictions: {
          maxFileSize: kUppyUploadMaxFileSizeBytes,
          allowedFileTypes: [...kUppyUploadAllowedFileTypes],
        },
        autoProceed: false,
      })
  )

  const uploadTasksRef = useRef<Record<string, ActiveUploadTask>>({})
  const isStartingUploadRef = useRef(false)

  useEffect(() => {
    const handleFileAdded = (file: AnyUppyFile) => {
      const queuedFolderId = file.meta.folderId as string | undefined
      const clientTmpId = (file.meta.clientTmpId as string | undefined) ?? nanoid()

      if (!queuedFolderId) {
        console.error('Cannot add file: no folder selected')
        uppy.removeFile(file.id)
        return
      }

      dispatch(
        UppyFileUploadActions.fileQueued({
          uppyFileId: file.id,
          clientTmpId,
          filename: file.name,
          fileSize: file.size ?? 0,
          folderId: queuedFolderId,
        })
      )
    }

    uppy.on('file-added', handleFileAdded)
    return () => {
      uppy.off('file-added', handleFileAdded)
    }
  }, [uppy, dispatch])

  const queuedFiles = useAppSelector(selectQueuedFiles)
  const isUploading = useAppSelector(selectIsUploading)

  const startFallbackUpload = useCallback(
    async (files: FileUploadState[]) => {
      if (files.length === 0) return
      if (isStartingUploadRef.current) return

      isStartingUploadRef.current = true

      try {
        const storageOptions = { bucketUrl: kGcsUserFilesPipelineBucketRef }
        const maxConcurrentUploads = usesStorageEmulator(storageOptions) ? kUppyUploadMaxConcurrentEmulatorUploads : kUppyUploadMaxConcurrentUploads

        await runUppyUploadPipeline({
          files,
          initUploadBatch,
          maxConcurrentUploads,
          getBlob: ({ uppyFileId }) => {
            const uppyFile = uppy.getFile(uppyFileId)
            return (uppyFile?.data as Blob | undefined) ?? null
          },
          uploadBlob: ({ clientTmpId, uploadTarget, blob, onProgress }) =>
            uploadInitializedUserFileBlob({
              clientTmpId,
              uploadTarget,
              blob,
              onProgress,
              uploadTasksRef,
            }),
          onFileInitializing: (clientTmpId) => {
            dispatch(UppyFileUploadActions.fileInitializing({ clientTmpId }))
          },
          onFileInitialized: ({ clientTmpId, serverFileId, storagePath }) => {
            dispatch(UppyFileUploadActions.fileInitialized({ clientTmpId, serverFileId, storagePath }))
          },
          onUploadStarted: (clientTmpId) => {
            dispatch(UppyFileUploadActions.uploadStarted({ clientTmpId }))
          },
          onUploadProgress: ({ clientTmpId, progress }) => {
            dispatch(UppyFileUploadActions.uploadProgress({ clientTmpId, progress }))
          },
          onUploadCompleted: (clientTmpId) => {
            dispatch(UppyFileUploadActions.uploadCompleted({ clientTmpId }))
          },
          onUploadFailed: ({ clientTmpId, error }) => {
            dispatch(UppyFileUploadActions.uploadFailed({ clientTmpId, error }))
          },
          onUploadBlobConsumed: ({ uppyFileId }) => {
            uppy.removeFile(uppyFileId)
          },
        })
      } finally {
        isStartingUploadRef.current = false
      }
    },
    [uppy, dispatch]
  )

  useEffect(() => {
    if (queuedFiles.length > 0 && !isUploading) {
      void startFallbackUpload(queuedFiles)
    }
  }, [queuedFiles, isUploading, startFallbackUpload])

  return <FileUploadContext.Provider value={{ uppy, uploadTasksRef, isStartingUploadRef }}>{children}</FileUploadContext.Provider>
}
